/**
 * Dashboard.jsx - Personalized User Dashboard
 * ============================================
 * - Personalized by business_type
 * - "Continue where you left off" section
 * - Max 3 visible actions (no cognitive overload)
 * - Data from Supabase
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import supabase from '../lib/supabase';
import { 
  LayoutGrid, 
  Camera, 
  FolderKanban, 
  Clock, 
  ArrowRight, 
  Sparkles,
  Building2,
  Home,
  Search,
  Eye,
  Info
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getMaterialImageUrl, PLACEHOLDER_IMAGE } from '../utils/materialImages';

// Business type configurations for personalized CTAs
const BUSINESS_CONFIG = {
  architect_designer: {
    greeting: 'Ciao',
    subtitle: 'Trova i materiali perfetti per i tuoi progetti',
    primaryAction: {
      title: 'Visual Match',
      description: 'Carica una foto per trovare materiali simili',
      icon: Camera,
      href: '/identify',
      tooltip: 'Carica una foto di una lastra per trovare alternative simili'
    },
    secondaryActions: [
      { title: 'Esplora Materiali', icon: LayoutGrid, href: '/materials' },
      { title: 'I Miei Progetti', icon: FolderKanban, href: '/projects' }
    ]
  },
  company_supplier: {
    greeting: 'Bentornato',
    subtitle: 'Gestisci il tuo catalogo e le richieste',
    primaryAction: {
      title: 'Gestisci Catalogo',
      description: 'Aggiungi e modifica i tuoi materiali',
      icon: LayoutGrid,
      href: '/materials',
      tooltip: 'Visualizza e gestisci il catalogo materiali'
    },
    secondaryActions: [
      { title: 'Visual Match', icon: Camera, href: '/identify' },
      { title: 'Analytics', icon: Eye, href: '/analytics' }
    ]
  },
  private_client: {
    greeting: 'Ciao',
    subtitle: 'Scopri i materiali per la tua casa',
    primaryAction: {
      title: 'Cerca per Foto',
      description: 'Scatta una foto per trovare la pietra giusta',
      icon: Camera,
      href: '/identify',
      tooltip: 'Carica una foto di una lastra per trovare alternative simili'
    },
    secondaryActions: [
      { title: 'Sfoglia Materiali', icon: LayoutGrid, href: '/materials' },
      { title: 'I Miei Salvati', icon: FolderKanban, href: '/collections' }
    ]
  }
};

// Default config fallback
const DEFAULT_CONFIG = BUSINESS_CONFIG.architect_designer;

const Dashboard = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [recentSearches, setRecentSearches] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [showTooltip, setShowTooltip] = useState(null);
  
  // Get config based on business_type
  const businessType = profile?.business_type || profile?.role_professional || 'architect_designer';
  const config = BUSINESS_CONFIG[businessType] || DEFAULT_CONFIG;
  
  // Get user display name
  const displayName = profile?.full_name || profile?.first_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Designer';
  
  // Fetch recent activity from Supabase
  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user?.id || !supabase) {
        setLoading(false);
        return;
      }
      
      try {
        // Try to fetch recent visual searches (table may not exist)
        try {
          const { data: searches } = await supabase
            .from('visual_searches')
            .select('id, created_at, query_image_url, top_result_name, top_result_id, top_result_score')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(3);
          
          if (searches) setRecentSearches(searches);
        } catch (e) {
          // Table doesn't exist yet - that's ok
          console.log('Dashboard: visual_searches table not available');
        }
        
        // Try to fetch recently viewed materials (table may not exist)
        try {
          const { data: viewed } = await supabase
            .from('material_views')
            .select(`
              id,
              created_at,
              material_id,
              materials (
                id,
                name,
                category,
                image_url,
                image_main
              )
            `)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(4);
          
          if (viewed) setRecentlyViewed(viewed.filter(v => v.materials));
        } catch (e) {
          // Table doesn't exist yet - that's ok
          console.log('Dashboard: material_views table not available');
        }
        
      } catch (err) {
        console.error('Dashboard: Failed to fetch activity', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecentActivity();
  }, [user?.id]);
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  const hasRecentActivity = recentSearches.length > 0 || recentlyViewed.length > 0;
  
  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          
          {/* Welcome Section */}
          <div className="mb-12">
            <p className="text-[var(--gold)] text-sm font-medium mb-2 uppercase tracking-wider">
              {config.greeting}
            </p>
            <h1 className="text-4xl md:text-5xl font-light text-white mb-3" data-testid="dashboard-welcome">
              {displayName}
            </h1>
            <p className="text-white/50 text-lg">
              {config.subtitle}
            </p>
          </div>
          
          {/* Continue Where You Left Off */}
          {hasRecentActivity && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Clock className="w-5 h-5 text-[var(--gold)]" />
                <h2 className="text-xl font-medium text-white">Continua da dove hai lasciato</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Recent Visual Searches */}
                {recentSearches.slice(0, 2).map((search) => (
                  <Link
                    key={search.id}
                    to={`/materials/${search.top_result_id}`}
                    className="group bg-[#12121a] border border-white/10 rounded-xl p-4 hover:border-[var(--gold)]/50 transition-all"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[var(--gold)]/10 flex items-center justify-center">
                        <Search className="w-5 h-5 text-[var(--gold)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/40 text-xs">Ricerca Visual</p>
                        <p className="text-white font-medium truncate">{search.top_result_name || 'Risultato'}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/30 text-xs">
                        {new Date(search.created_at).toLocaleDateString('it-IT')}
                      </span>
                      {search.top_result_score && (
                        <span className="text-[var(--gold)] text-xs font-medium">
                          {Math.round(search.top_result_score)}% match
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
                
                {/* Recently Viewed Materials */}
                {recentlyViewed.slice(0, 2).map((view) => (
                  <Link
                    key={view.id}
                    to={`/materials/${view.material_id}`}
                    className="group bg-[#12121a] border border-white/10 rounded-xl overflow-hidden hover:border-[var(--gold)]/50 transition-all"
                  >
                    <div className="aspect-[16/10] bg-[#1a1a2e] relative">
                      <img 
                        src={getMaterialImageUrl(view.materials)}
                        alt={view.materials?.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                      />
                      <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded text-white/70 text-xs flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        Visto di recente
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-white/40 text-xs mb-1">{view.materials?.category}</p>
                      <p className="text-white font-medium truncate">{view.materials?.name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          
          {/* Primary Action - Hero CTA */}
          <section className="mb-8">
            <Link
              to={config.primaryAction.href}
              className="group relative block bg-gradient-to-br from-[#1a1a2e] to-[#12121a] border border-white/10 rounded-2xl p-8 md:p-12 overflow-hidden hover:border-[var(--gold)]/50 transition-all"
              data-testid="primary-action"
              onMouseEnter={() => setShowTooltip('primary')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--gold)]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-[var(--gold)] flex items-center justify-center shrink-0">
                    <config.primaryAction.icon className="w-8 h-8 text-[#0a0a0f]" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-medium text-white mb-2">
                      {config.primaryAction.title}
                    </h3>
                    <p className="text-white/50 text-lg">
                      {config.primaryAction.description}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 text-[var(--gold)] font-medium group-hover:gap-4 transition-all">
                  Inizia ora
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
              
              {/* Tooltip */}
              {showTooltip === 'primary' && config.primaryAction.tooltip && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-4 py-2 bg-[#1a1a2e] border border-white/20 rounded-lg shadow-xl z-10 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-white/80 text-sm">
                    <Info className="w-4 h-4 text-[var(--gold)]" />
                    {config.primaryAction.tooltip}
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-[#1a1a2e]" />
                </div>
              )}
            </Link>
          </section>
          
          {/* Secondary Actions - 2 cards max */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            {config.secondaryActions.slice(0, 2).map((action, index) => (
              <Link
                key={action.href}
                to={action.href}
                className="group bg-[#12121a] border border-white/10 rounded-xl p-6 hover:border-[var(--gold)]/50 transition-all flex items-center gap-4"
                data-testid={`secondary-action-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-[var(--gold)]/10 transition-colors">
                  <action.icon className="w-6 h-6 text-white/60 group-hover:text-[var(--gold)] transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-white group-hover:text-[var(--gold)] transition-colors">
                    {action.title}
                  </h3>
                </div>
                <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-[var(--gold)] group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </section>
          
          {/* Empty State - No Recent Activity */}
          {!hasRecentActivity && (
            <section className="bg-[#12121a] border border-white/10 rounded-xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--gold)]/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-[var(--gold)]" />
              </div>
              <h3 className="text-xl font-medium text-white mb-2">
                Inizia a esplorare
              </h3>
              <p className="text-white/50 mb-6 max-w-md mx-auto">
                Non hai ancora attività recente. Inizia a esplorare i materiali o usa Visual Match per trovare la pietra perfetta.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/materials"
                  className="px-6 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Esplora Materiali
                </Link>
                <Link
                  to="/identify"
                  className="px-6 py-3 bg-[var(--gold)] text-[#0a0a0f] rounded-lg hover:bg-[#d4b896] transition-colors font-medium"
                >
                  Prova Visual Match
                </Link>
              </div>
            </section>
          )}
          
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Dashboard;
